// 画面の大きさへの合わせ方（ゲームの「基準解像度＋UIの拡大率」。Unity の Canvas Scaler・Cocos の design resolution と同じ考え方）。
// CSS は基準の大きさ（スマホ縦 390×660）のときの px で書く。ビルド時にこのプラグインが
//   1) 宣言の px を rem に直す（1px 以下の細線と、大文字の PX はそのまま＝わざと固定したいとき）
//   2) 画面の幅・高さ・縦横比・向きの @media を、html のクラス（mq-…）の条件に直す。
//      例：@media (max-width: 899px) and (max-height: 760px) { .a {…} } → :where(html.mq-w-le-899_h-le-760) .a {…}
//      クラスは src/ui/viewport.ts が「拡大率で割った基準の座標（帯の幅・高さ）」で判定して付け外しする。
//      （@container は使わない：古いブラウザ（Safari 15 など）で効かず、スマホでもPCでもない崩れた見た目になったため）
//      :where() なので詳細度は元のまま。
//   3) 部品ごとの @container の px も rem に
// 根の文字の大きさ（html の font-size）は viewport.ts が 15px × 拡大率 にする。くわしくは Documentation/UI_SCALING.md。
import type { AtRule, Declaration, Plugin, Rule } from 'postcss';

/** 拡大率 1 のときの根の文字の大きさ（px） */
export const ROOT_PX = 15;
const toRem = (px: number) => `${Number((px / ROOT_PX).toFixed(4))}rem`;
const PX = /(-?\d*\.?\d+)px\b/g;
/** 画面の大きさで変わる @media（幅・高さ・縦横比・向き）。好み（prefers-*）や入力（pointer・hover）はそのまま @media */
const VIEWPORT = /\((?:min-|max-)?(?:width|height|aspect-ratio)\s*:|\(orientation\s*:/;
const NON_VIEWPORT = /prefers-|pointer|hover|display-mode|print|resolution/;

/** @media の条件 → クラス名の鍵（viewport.ts の mqMatches が読む）。読めない条件は null */
export function mqKey(params: string): string | null {
  const parts: string[] = [];
  for (const c of params.split(/\s+and\s+/)) {
    const m = /^\(\s*(min-|max-)?(width|height|aspect-ratio|orientation)\s*:\s*([^)]+?)\s*\)$/.exec(c.trim());
    if (!m) return null;
    const [, mm, feat, raw] = m;
    if (feat === 'orientation') {
      parts.push(`or-${raw}`);
      continue;
    }
    const op = mm === 'min-' ? 'ge' : mm === 'max-' ? 'le' : null;
    if (!op) return null;
    if (feat === 'aspect-ratio') {
      const r = /^(\d+)\s*\/\s*(\d+)$/.exec(raw);
      if (!r) return null;
      parts.push(`ar-${op}-${r[1]}-${r[2]}`);
    } else {
      const px = /^(\d+(?:\.\d+)?)px$/.exec(raw);
      if (!px) return null;
      parts.push(`${feat === 'width' ? 'w' : 'h'}-${op}-${px[1].replace('.', 'p')}`);
    }
  }
  return parts.length ? `mq-${parts.join('_')}` : null;
}

/** セレクタの前に条件のクラスを付ける（html／:root で始まるものはその要素に付ける） */
function scope(sel: string, cls: string): string {
  return sel
    .split(/,(?![^()]*\))/)
    .map((s) => {
      const t = s.trim();
      const m = /^(html|:root)(.*)$/.exec(t);
      if (m) return `${m[1]}:where(.${cls})${m[2]}`;
      return `:where(html.${cls}) ${t}`;
    })
    .join(', ');
}

export function uiScale(): Plugin {
  return {
    postcssPlugin: 'hk-ui-scale',
    Declaration(decl: Declaration) {
      if (!decl.value.includes('px')) return;
      // html の文字の大きさは viewport.ts が決める（ここだけは px のまま）
      const rule = decl.parent as { selector?: string } | undefined;
      if (decl.prop === 'font-size' && rule?.selector && /(^|,)\s*html\s*(,|$)/.test(rule.selector)) return;
      decl.value = decl.value.replace(PX, (m, n: string) => (Math.abs(Number(n)) <= 1 ? m : toRem(Number(n))));
    },
    AtRule: {
      media(at: AtRule) {
        if (!VIEWPORT.test(at.params) || NON_VIEWPORT.test(at.params) || at.params.includes(',')) return;
        const key = mqKey(at.params);
        if (!key) return;
        // 中の規則にクラスの条件を付けて、@media を外す（@keyframes などはそのまま外に出す）
        at.walkRules((r: Rule) => {
          if (r.parent?.type === 'atrule' && /keyframes/.test((r.parent as AtRule).name)) return;
          r.selector = scope(r.selector, key);
        });
        at.replaceWith(at.nodes ?? []);
      },
      // 部品ごとの @container は基準の座標で
      container(at: AtRule) {
        at.params = at.params.replace(PX, (_m, n: string) => toRem(Number(n)));
      },
    },
  };
}
uiScale.postcss = true;
